import React, { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useParams, useNavigate } from 'react-router';

import Auth from '../../../../api/auth';
import useOrgPermissions from '../../../../components/AppLayout/hooks/useOrgPermissions';
import Table from '../../../../components/Table';
import rbacCheck from '../../../../rbac/rbacCheck';

import CreateProductFormModal from './CreateProductFormModal';

import useTableApiCall from '@/hooks/useTableApiCall';
import { useUserData } from '@/redux/selectors';
import type { Product } from '@/types/productTypes';
import type { TableRowData } from '@/types/tableTypes';

interface ProductsProps {
  orgId?: string;
}
const Products = ({ orgId }: ProductsProps) => {
  const { projectId } = useParams();
  const intl = useIntl();
  const navigate = useNavigate();
  const userData = useUserData();
  const { isAdminOrg } = useOrgPermissions(orgId);

  const [createModalOpened, setCreateModalOpened] = useState<boolean>(false);
  const [products, setProducts] = useState<Product[] | null>(null);
  const [rows, setRows] = useState<TableRowData[]>([]);
  const [itemsPerPage, setItemsPerPage] = useState(5);

  const transformProjectsToRows = useCallback(
    (data: Product[]) => {
      setProducts(data);
      return data.map(product => ({
        key: product._id,
        onRowClick: () => navigate(`/products/${product._id}`),
        data: [
          {
            field: 'name',
            value: product.name,
          },
          {
            field: 'description',
            value: product.description,
          },
          {
            field: 'type',
            value: product.type,
          },
          {
            field: 'juiceGroup',
            value: product.details.juiceGroup,
          },
          {
            field: 'tags',
            type: 'tags',
            value: product.details.tags,
          },
        ],
      }));
    },
    [navigate]
  );

  const handleRowsUpdate = useCallback(
    (data: Product[]) => {
      setRows(transformProjectsToRows(data) as TableRowData[]);
    },
    [transformProjectsToRows]
  );

  const { fetchData, setCurrentPage, ...tableData } = useTableApiCall(
    Auth.fetchV2Products as unknown as (...args: unknown[]) => Promise<unknown>,
    {
      isPaginated: true,
      setRows: handleRowsUpdate as (rows: unknown[]) => void,
      searchColumns: ['name'],
      endpointParams: {
        projectId: projectId,
        limit: itemsPerPage,
      },
    }
  );

  const canCreateProduct = useMemo(
    () =>
      !isAdminOrg &&
      (products?.length === 0 || products?.some(product => product.type !== 'evaluation')) &&
      rbacCheck(userData.roles, '/products:POST'),
    [isAdminOrg, products, userData.roles]
  );

  const fields = [
    {
      title: 'Name',
      id: 'name',
    },
    {
      title: 'Description',
      id: 'description',
    },
    {
      title: 'Type',
      id: 'type',
      type: 'options',
    },
    {
      title: 'Juicegroup',
      id: 'juiceGroup',
    },
    {
      title: 'Tags',
      id: 'tags',
      type: 'options',
    },
  ];

  const getCreateProductTooltip = (): string => {
    if (isAdminOrg) {
      return intl.formatMessage({ id: 'adminOrgActionDisabled' });
    }

    const evaluationProductExists = products?.some(product => product.type === 'evaluation');
    return evaluationProductExists
      ? intl.formatMessage({ id: 'evaluationProductCreationDisabled' })
      : intl.formatMessage({ id: 'productCreationPermissionErr' });
  };

  const handlePageChange = useCallback(
    (newPage: number) => {
      setCurrentPage(newPage);
    },
    [setCurrentPage]
  );

  return (
    <>
      {createModalOpened && (
        <CreateProductFormModal
          onSubmit={newProduct => {
            setProducts(prev => {
              const availableProducts = [...(prev ?? []), newProduct as Product];
              handleRowsUpdate(availableProducts);
              return availableProducts;
            });
          }}
          onCancel={() => {
            setCreateModalOpened(false);
          }}
        />
      )}
      <Table
        key={`products-table`}
        header='Products'
        fields={fields}
        rows={rows}
        pagination
        {...tableData}
        pageSize={itemsPerPage}
        search
        advancedSearch={false}
        setItemsPerPage={(size: number) => {
          setItemsPerPage(size);
        }}
        setFilter={tableData.setFilter as (filter: unknown) => void}
        onPageChange={handlePageChange}
        createAction={{
          disabled: !canCreateProduct,
          onClick: () => {
            setCreateModalOpened(true);
          },
          tooltip: canCreateProduct ? '' : getCreateProductTooltip(),
        }}
        error={tableData.error ? tableData.error.message : null}
      />
    </>
  );
};

export default React.memo(Products);
